<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';

const props = defineProps({
  caseData: { type: Object, required: true },
});

const emit = defineEmits(['action']);
const { t } = useI18n();
const attentionBuckets = ['amarelos', 'laranjas', 'vermelhos'];
const bucketClasses = {
  amarelos: 'border-[#FFC43E] bg-white text-lais-text-body',
  laranjas: 'border-[#FF7A11] bg-white text-lais-text-body',
  vermelhos: 'border-[#E33838] bg-[#FDF2F2] text-[#E33838]',
};
const counterClasses = {
  amarelos: 'border-transparent bg-[#FFFAF0] text-[#D69500]',
  laranjas: 'border-transparent bg-[#FFF1E5] text-[#FF7A11]',
  vermelhos: 'border-transparent bg-[#FCE9E9] text-[#E33838]',
};
const bucketLabels = {
  amarelos: () => t('POST_SALE.DEADLINES.AMARELOS'),
  laranjas: () => t('POST_SALE.DEADLINES.LARANJAS'),
  vermelhos: () => t('POST_SALE.DEADLINES.VERMELHOS'),
};
const actionLabels = {
  generate_itbi_document: () =>
    t('POST_SALE.AGENDA.ACTIONS.GENERATE_ITBI_DOCUMENT'),
  request_service: () => t('POST_SALE.AGENDA.ACTIONS.REQUEST_SERVICE'),
  schedule_deed: () => t('POST_SALE.AGENDA.ACTIONS.SCHEDULE_DEED'),
};
const actionLabelKeys = {
  'POST_SALE.AGENDA.ACTIONS.GENERATE_ITBI_DOCUMENT':
    actionLabels.generate_itbi_document,
  'POST_SALE.AGENDA.ACTIONS.GENERATE_ITBI_NEW_DOCUMENT': () =>
    t('POST_SALE.AGENDA.ACTIONS.GENERATE_ITBI_NEW_DOCUMENT'),
};
const messageKeys = {
  'POST_SALE.DEADLINES.ITBI_PROTOCOL_EXPIRED': values =>
    t('POST_SALE.DEADLINES.ITBI_PROTOCOL_EXPIRED', values),
  'POST_SALE.DEADLINES.ITBI_PROTOCOL_EXPIRES_TODAY': () =>
    t('POST_SALE.DEADLINES.ITBI_PROTOCOL_EXPIRES_TODAY'),
  'POST_SALE.DEADLINES.ITBI_GUIDE_DUE': values =>
    t('POST_SALE.DEADLINES.ITBI_GUIDE_DUE', values),
  'POST_SALE.DEADLINES.ITBI_GUIDE_AVAILABLE': () =>
    t('POST_SALE.DEADLINES.ITBI_GUIDE_AVAILABLE'),
  'POST_SALE.DEADLINES.ITBI_PENDING_OVERDUE': values =>
    t('POST_SALE.DEADLINES.ITBI_PENDING_OVERDUE', values),
  'POST_SALE.DEADLINES.ITBI_PENDING_OVERDUE_TODAY': () =>
    t('POST_SALE.DEADLINES.ITBI_PENDING_OVERDUE_TODAY'),
  'POST_SALE.DEADLINES.ITBI_PENDING_DUE': values =>
    t('POST_SALE.DEADLINES.ITBI_PENDING_DUE', values),
  'POST_SALE.DEADLINES.ITBI_PENDING_ATTENTION': () =>
    t('POST_SALE.DEADLINES.ITBI_PENDING_ATTENTION'),
};

const sourceDeadlines = computed(() => {
  if (Array.isArray(props.caseData.deadlines)) return props.caseData.deadlines;
  return props.caseData.deadline ? [props.caseData.deadline] : [];
});
const groups = computed(() =>
  attentionBuckets
    .map(bucket => ({
      bucket,
      deadlines: sourceDeadlines.value.filter(item => item.bucket === bucket),
    }))
    .filter(group => group.deadlines.length)
);
const message = deadline => {
  const values = {
    days: Math.abs(Number(deadline.days_remaining || 0)),
    status: props.caseData.status_label,
    ...(deadline.message || {}),
  };
  if (messageKeys[deadline.message_key])
    return messageKeys[deadline.message_key](values);
  if (deadline.bucket === 'vermelhos')
    return t('POST_SALE.DEADLINES.MESSAGE_OVERDUE', values);
  if (deadline.bucket === 'laranjas')
    return t('POST_SALE.DEADLINES.MESSAGE_DUE_SOON', values);
  return t('POST_SALE.DEADLINES.MESSAGE_ATTENTION', values);
};
const actionLabel = action => {
  const translate =
    actionLabelKeys[action?.label_key] || actionLabels[action?.code];
  return translate?.() || '';
};
</script>

<template>
  <div class="contents">
    <LaisCard
      v-if="groups.length"
      :title="t('POST_SALE.DETAIL.DEADLINE_MAP')"
      prefix-icon="i-lucide-calendar-clock"
    >
      <div class="flex flex-wrap items-center gap-2">
        <span
          v-for="group in groups"
          :key="group.bucket"
          data-testid="post-sale-deadline-count"
          class="inline-flex rounded-full border px-2 py-1 text-xs font-semibold"
          :class="counterClasses[group.bucket]"
          :title="bucketLabels[group.bucket]()"
        >
          {{ group.deadlines.length }}
        </span>
      </div>
      <div class="mt-3 grid gap-[10px] px-5 pb-5">
        <template v-for="group in groups" :key="group.bucket">
          <div
            v-for="deadline in group.deadlines"
            :key="deadline.code"
            data-testid="post-sale-deadline-row"
            class="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-[30px]"
          >
            <div
              class="flex w-full items-start gap-2 rounded-[10px] border p-2 text-sm sm:w-[70%]"
              :class="bucketClasses[group.bucket]"
            >
              <span
                class="i-lucide-circle-alert mt-0.5 size-4 shrink-0"
                aria-hidden="true"
              />
              <p>{{ message(deadline) }}</p>
            </div>
            <LaisPrimaryButton
              v-if="deadline.action && actionLabel(deadline.action)"
              class="w-full sm:w-auto"
              size="sm"
              @click="emit('action', deadline.action)"
            >
              {{ actionLabel(deadline.action) }}
            </LaisPrimaryButton>
          </div>
        </template>
      </div>
    </LaisCard>
  </div>
</template>
